/**
 * useChatUnreadCount — polls the chat app's unread message count via the query registry.
 *
 * The chat app registers an `unreadCount` query handler that returns
 * `{ count: number, conversationCount: number }`. This hook polls it on a
 * short interval so the shell header badge stays current without requiring
 * the chat app to be the active app.
 *
 * Data sovereignty note: we never send unread counts to any server — this is
 * purely local, derived from the user's own CRDT-backed message store.
 */

import { useState, useEffect, useRef } from 'react';
import type { Runtime } from '@yada/runtime';

const POLL_INTERVAL_MS = 5_000; // 5s — snappy enough without hammering localStorage

export interface ChatUnreadResult {
  /** Total unread message count across all conversations */
  unreadCount: number;
}

export function useChatUnreadCount(runtime: Runtime): ChatUnreadResult {
  const [unreadCount, setUnreadCount] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      try {
        const result = await runtime.queryRegistry.query<{ count: number }>(
          '@yada/app-chat',
          'unreadCount'
        );
        if (!cancelled) {
          setUnreadCount(result?.count ?? 0);
        }
      } catch {
        // Query registry not ready or chat not mounted — silent
      }
    };

    // Initial poll
    poll();

    intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [runtime]);

  return { unreadCount };
}
